import { styled, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import { Grid, Button, CircularProgress, Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useNavigate, useLocation } from 'react-router-dom';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { useOktaAuth } from '@okta/okta-react';
import { useContext, useEffect, useState } from 'react';
import { getUserConversations } from '../providers/chatbotApi';
import { toast } from 'react-toastify';
import IConversationHistory from '../models/iConversationHistory';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { MessageContext } from '../context/MessageContext';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { useIdentity, useHasRole } from '../config/authConfig';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import { getAppFromPath } from '../enum/UrlAppMap';
import { Roles } from '../enum/Roles';

interface DrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  setScrollToNewest: (scrollToNewest: boolean) => void;
  showEsaInfo: boolean;
}

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: 0,
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    position: 'relative',
    height: '100%',
  },
  variants: [
    {
      props: { open: true },
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': { ...openedMixin(theme), position: 'relative', height: '100%' },
      },
    },
    {
      props: { open: false },
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': { ...closedMixin(theme), position: 'relative', height: '100%' },
      },
    },
  ],
}));

export default function PersistentCollapsibleDrawer(props: DrawerProps) {
  const { authState } = useOktaAuth();
  const { open, setOpen } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const currentApp = getAppFromPath(location.pathname);
  const [conversations, setConversations] = useState<IConversationHistory[]>([]);
  const [numberOfRecentConversations, setNumberOfRecentConversations] = useState<number>(5);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setMessages, chatId, setChatId, setGeminiChat } = useContext(MessageContext);
  const { isAdmin } = useIdentity();

  const isProductFinderAdmin = useHasRole([Roles.ProductFinderAdmin]);

  useEffect(() => {
    const getRecentChats = async () => {
      if (authState?.accessToken?.accessToken && props.showEsaInfo) {
        try {
          setIsLoading(true);
          const conversation = await getUserConversations(
            authState.accessToken.accessToken,
            numberOfRecentConversations
          );
          setConversations(conversation);
        } catch (error) {
          toast.error('An error occurred while fetching conversation history');
        }
        setIsLoading(false);
      }
    };
    getRecentChats();
  }, [numberOfRecentConversations, authState?.accessToken?.accessToken, chatId, props.showEsaInfo]);

  const handleConversationClick = (conversation: IConversationHistory) => {
    props.setScrollToNewest(false);
    setMessages(conversation.conversationData);
    setGeminiChat([]);
    setChatId(conversation.id);
  };

  const handleNewChat = () => {
    setMessages([]);
    setGeminiChat([]);
    setChatId(undefined);
    navigate('/chat');
  };

  return (
    <Drawer variant="permanent" open={open}>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          px: 2,
          pt: 2,
        }}
      >
        <Grid
          container
          sx={{
            flexGrow: 1,
          }}
        >
          {props.showEsaInfo && (
            <Grid size={12}>
              <Button
                variant="outlined"
                onClick={handleNewChat}
                sx={{
                  padding: 0,
                  margin: 0,
                  minWidth: 'fit-content',
                  borderRadius: open ? '15px' : '50%',
                  p: 0.5,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                }}
              >
                <AddIcon
                  sx={{
                    color: '#03387E',
                    fontSize: '1.5em',
                  }}
                />
                {open && (
                  <Typography
                    sx={{
                      m: 0,
                      p: 0,
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      mx: 1,
                    }}
                  >
                    New Chat
                  </Typography>
                )}
              </Button>
              {open && (
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      mt: 2,
                    }}
                  >
                    Recent
                  </Typography>
                  {isLoading ? (
                    <CircularProgress />
                  ) : (
                    <>
                      <Box
                        sx={{
                          maxHeight: 'calc(100vh - 350px)',
                          overflowY: 'auto',
                        }}
                      >
                        {conversations.map((chat, i) => (
                          <Box key={i}>
                            <IconButton sx={{ maxWidth: '100%' }} onClick={() => handleConversationClick(chat)}>
                              <ChatBubbleIcon />
                              <Typography
                                sx={{
                                  ml: 1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {chat.conversationData[0].text}
                              </Typography>
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                      {conversations.length === numberOfRecentConversations && (
                        <Box>
                          <IconButton onClick={() => setNumberOfRecentConversations(numberOfRecentConversations + 10)}>
                            <KeyboardArrowDownIcon />
                            <Typography
                              sx={{
                                ml: 1,
                              }}
                            >
                              Show More
                            </Typography>
                          </IconButton>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Grid>
          )}
        </Grid>
        <Grid container>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px', // Adds space between the bottom icons
              justifyContent: 'flex-start',
              paddingBottom: '2em',
              alignItems: 'flex-start',
              width: '100%',
            }}
          >
            {open && (
              <>
                <Button
                  sx={{
                    background: '#03387E',
                    '&:hover': {
                      backgroundColor: '#03387E',
                    },
                    width: '100%',
                    color: 'white',
                    display: 'none',
                  }}
                >
                  <Typography
                    sx={{
                      textTransform: 'none',
                    }}
                  >
                    Action 1
                  </Typography>
                </Button>
                <Button
                  sx={{
                    background: '#03387E',
                    '&:hover': {
                      backgroundColor: '#03387E',
                    },
                    width: '100%',
                    color: 'white',
                    display: 'none',
                  }}
                >
                  <Typography
                    sx={{
                      textTransform: 'none',
                    }}
                  >
                    Action 2
                  </Typography>
                </Button>
              </>
            )}
            <Tooltip title="Help" placement="right">
              <IconButton
                aria-label="help"
                onClick={() => { navigate('/help', { state: { from: currentApp } }); props.setOpen(false); }}
                sx={{
                  pl: 0,
                }}
              >
                <HelpOutlineIcon />
                {open && <Typography sx={{ paddingLeft: '0.5em' }}>Help</Typography>}
              </IconButton>
            </Tooltip>
            {isAdmin && (
              <Tooltip title="Manage Users" placement="right">
                <IconButton
                  aria-label="users"
                  onClick={() =>{ navigate('/users'); props.setOpen(false);}}
                  sx={{
                    pl: 0,
                  }}
                >
                  <GroupAddIcon />
                  {open && <Typography sx={{ paddingLeft: '0.5em' }}>Manage Users</Typography>}
                </IconButton>
              </Tooltip>
            )}
            {(isAdmin || isProductFinderAdmin) && (
              <Tooltip title="Manage Custom Rankings" placement="right">
                <IconButton
                  aria-label="custom-rankings"
                  onClick={() => { navigate('/product-finder/custom-rankings'); props.setOpen(false); }}
                  sx={{
                    pl: 0,
                  }}
                >
                  <StarBorderIcon />
                  {open && <Typography sx={{ paddingLeft: '0.5em' }}>Manage Custom Rankings</Typography>}
                </IconButton>
              </Tooltip>
            )}
            {(isAdmin || isProductFinderAdmin) && (
              <Tooltip title="Manage ETOs" placement="right">
                <IconButton
                  aria-label="etos"
                  onClick={() => { navigate('/product-finder/etos'); props.setOpen(false); }}
                  sx={{
                    pl: 0,
                  }}
                >
                  <EngineeringOutlinedIcon />
                  {open && <Typography sx={{ paddingLeft: '0.5em' }}>Manage ETOs</Typography>}
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Grid
            sx={{
              margin: '0 2em',
            }}
            size={12}>
            {open && authState?.idToken?.claims.name && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  position: 'absolute',
                  bottom: '5px',
                  //centered
                  left: '1.25rem',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: 'black',
                  }}
                >
                  Logged In: {authState?.idToken?.claims.name}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
}
